/**
 * Sri Lanka NIC + EPF member-number FORMAT validation.
 *
 * Malformed NIC and EPF member numbers must never be persisted and then fed
 * verbatim into EPF/ETF statutory filings. These pure validators reject the
 * structurally-invalid shapes. Moved to @jumpstone/core (from apps/api) so the
 * API, the WagePro employee form, and the migration importer share one
 * implementation; `apps/api/src/lib/sl_nic_epf_validation.ts` re-exports this.
 *
 * Per the Department for Registration of Persons NIC scheme:
 *   - OLD format: 9 digits + a trailing check letter (V/v/X/x, case
 *     insensitive). Digits 1-2 encode a 2-digit birth year, digits 3-5 encode
 *     day-of-year (001-366 for males, +500 offset i.e. 501-866 for females),
 *     digits 6-9 are a serial.
 *   - NEW format: exactly 12 digits, no letter. Digits 1-4 encode birth year,
 *     digits 5-7 encode day-of-year with the same male/female ranges, digits
 *     8-12 are a serial.
 *
 * Matching is intentionally strict (no trimming). Surrounding whitespace,
 * wrong lengths, non-ASCII/full-width digit characters, a check letter in the
 * wrong position, and cross-format shapes (9-digit-no-letter, 12-digit-with-
 * letter) all fall through to the generic "unrecognised format" rejection
 * rather than being silently coerced into a valid shape.
 *
 * EPF member-number formats vary across employers, so the EPF check is
 * deliberately conservative: reject only the empty string and strings
 * containing control characters, leaving the shape otherwise unconstrained.
 */

export type SlValidationResult = { ok: true } | { ok: false; reason: string };

// ASCII digits only — `\d` under the default (non-unicode) flag already
// excludes full-width/unicode digits, which is the desired strictness.
const OLD_NIC_PATTERN = /^\d{9}[VXvx]$/;
const NEW_NIC_PATTERN = /^\d{12}$/;

/**
 * Day-of-year is valid for males (001-366) or females (+500 offset, 501-866).
 * 000, 367-500 and 867+ are rejected for either sex.
 */
function isValidDayOfYear(day: number): boolean {
  return (day >= 1 && day <= 366) || (day >= 501 && day <= 866);
}

/**
 * Validate a Sri Lanka NIC number (OLD 9-digit+letter or NEW 12-digit form).
 * Returns `{ ok: true }` when the value is structurally valid, otherwise
 * `{ ok: false, reason }` with a caller-safe message.
 */
export function validateSlNic(value: string): SlValidationResult {
  if (OLD_NIC_PATTERN.test(value)) {
    const day = Number(value.slice(2, 5));
    if (!isValidDayOfYear(day)) {
      return { ok: false, reason: 'nic_number has an invalid day-of-year' };
    }
    return { ok: true };
  }

  if (NEW_NIC_PATTERN.test(value)) {
    const day = Number(value.slice(4, 7));
    if (!isValidDayOfYear(day)) {
      return { ok: false, reason: 'nic_number has an invalid day-of-year' };
    }
    return { ok: true };
  }

  return {
    ok: false,
    reason:
      'nic_number must be a 9-digit NIC with a trailing V/X, or a 12-digit NIC',
  };
}

// eslint-disable-next-line no-control-regex -- deliberately matching control chars to reject them
const CONTROL_CHAR_PATTERN = /[\x00-\x1F\x7F]/;

/**
 * Validate a Sri Lanka EPF member number. Conservative by design: rejects the
 * empty string and any string containing control characters; otherwise the
 * shape is left unconstrained because employer EPF formats vary.
 */
export function validateSlEpfNumber(value: string): SlValidationResult {
  if (value.length === 0) {
    return { ok: false, reason: 'epf_member_no must not be empty' };
  }
  if (CONTROL_CHAR_PATTERN.test(value)) {
    return {
      ok: false,
      reason: 'epf_member_no must not contain control characters',
    };
  }
  return { ok: true };
}
